/**
 * Renders children when authenticated. Otherwise drives the embedded
 * passwordless flow:
 *
 *   unauthenticated  -> form: email input + "Email me the link" button
 *   link_sent        -> "Check your inbox" with re-send option
 *   loading          -> spinner (initial mount, refresh in flight)
 *   error            -> message + "Try again" button
 *   authenticated    -> children
 *
 * No Auth0 hosted page is ever shown.
 */

import { useState, type FormEvent, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useAuth } from "../providers/AuthProvider";
import { useI18n } from "../providers/I18nProvider";

export function LoginGate({ children }: { children: ReactNode }) {
  const { status, error, pendingEmail, startLogin, resetLogin } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  if (status === "authenticated") {
    return <>{children}</>;
  }

  if (status === "loading") {
    return (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  }

  if (status === "link_sent") {
    return (
      <Centered>
        <Brand />
        <Box
          sx={{
            mb: 2,
            mt: 2,
            width: 56,
            height: 56,
            borderRadius: "50%",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 28,
          }}
        >
          ✓
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t("login.checkInbox")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, textAlign: "center" }}>
          {t("login.linkSentPrefix")} <strong>{pendingEmail}</strong>. {t("login.linkSentSuffix")}
        </Typography>
        <Box sx={{ mt: 3 }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => {
              setEmail(pendingEmail ?? "");
              setLocalError(null);
              resetLogin();
            }}
          >
            {t("login.useDifferentEmail")}
          </Link>
        </Box>
      </Centered>
    );
  }

  if (status === "error") {
    return (
      <Centered>
        <Brand />
        <Alert severity="error" sx={{ mt: 2, maxWidth: 400 }}>
          {error || t("login.genericError")}
        </Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={resetLogin}>
          {t("login.tryAgain")}
        </Button>
      </Centered>
    );
  }

  // unauthenticated
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setLocalError(t("login.invalidEmail"));
      return;
    }
    setSubmitting(true);
    setLocalError(null);
    try {
      await startLogin(trimmed);
    } catch (err) {
      setLocalError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Centered>
      <Brand />
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, maxWidth: 360, textAlign: "center" }}
      >
        {t("login.intro")}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ width: 320 }}>
        <Stack spacing={2}>
          <TextField
            type="email"
            label={t("login.workEmail")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            autoFocus
            fullWidth
            size="medium"
          />
          {localError && <Alert severity="error">{localError}</Alert>}
          <Button type="submit" variant="contained" size="large" disabled={submitting} fullWidth>
            {submitting ? t("login.sending") : t("login.emailLink")}
          </Button>
        </Stack>
      </Box>
    </Centered>
  );
}

function Brand() {
  return (
    <Stack alignItems="center" spacing={2} sx={{ mb: 2 }}>
      {/* Real Paybyrd wordmark (gradient P + black "Paybyrd" type). */}
      <Box
        component="img"
        src="/paybyrd-wordmark.svg"
        alt="Paybyrd"
        sx={{ height: 36, width: "auto", display: "block" }}
      />
      <Typography
        variant="caption"
        sx={{
          fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
          fontSize: 11,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontWeight: 600,
          color: "primary.main",
        }}
      >
        Engage · Partner program
      </Typography>
    </Stack>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ minHeight: "100vh", p: 3 }}
      spacing={1}
    >
      {children}
    </Stack>
  );
}
